/**
 * Indexer Client
 *
 * Provides a validated, circuit-breaker-protected client for making
 * requests to the indexer. All frontend code should use this instead
 * of direct fetch calls.
 */

import { withCircuitBreaker } from './circuit-breaker'
import { IndexerTimeoutError, IndexerUnavailableError } from './errors'
import { extractAndValidatePathParams, isExpensivePath, validatePath } from './validation'

/** Default timeout for indexer requests */
const DEFAULT_TIMEOUT_MS = 10000

/** Base URL for the indexer proxy (goes through our API route) */
const INDEXER_PROXY_URL = '/api/indexer'

interface IndexerRequestOptions {
  /** Timeout in milliseconds */
  timeout?: number
  /** Skip validation (not recommended) */
  skipValidation?: boolean
  /** Custom headers */
  headers?: Record<string, string>
  /** Cache mode */
  cache?: RequestCache
  /** Next.js revalidate option */
  next?: { revalidate?: number | false; tags?: string[] }
}

/**
 * Make a validated request to the indexer through the proxy
 */
export async function indexerFetch<T = unknown>(path: string, options: IndexerRequestOptions = {}): Promise<T> {
  const { timeout = DEFAULT_TIMEOUT_MS, skipValidation = false, headers = {}, cache, next } = options

  // Validate path unless explicitly skipped
  if (!skipValidation) {
    validatePath(path)
    extractAndValidatePathParams(path)
  }

  // Build the proxy URL
  const url = `${INDEXER_PROXY_URL}${path.startsWith('/') ? path : `/${path}`}`

  // Use circuit breaker for the request
  return withCircuitBreaker(async () => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...headers,
        },
        signal: controller.signal,
        cache,
        next,
      })

      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After')
          throw new IndexerUnavailableError(path, new Error(`Rate limited. Retry after ${retryAfter ?? 60}s`))
        }
        throw new IndexerUnavailableError(path, new Error(`HTTP ${response.status}: ${response.statusText}`))
      }

      return response.json() as Promise<T>
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new IndexerTimeoutError(timeout, path)
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  })
}

/**
 * Fetch historic interest rate for a pooled market
 */
export async function fetchPooledAPY(chainId: number, address: string) {
  return indexerFetch(`/api/chains/${chainId}/pooled/${address}/apy`, {
    timeout: 15000, // Expensive operation, longer timeout
    next: { revalidate: 300 }, // Cache for 5 minutes
  })
}

/**
 * Fetch 30d average supply APY for all pooled market assets.
 */
export async function fetchPooledAverageAPY(chainId: number) {
  return indexerFetch(`/api/chains/${chainId}/pooled/apy`, {
    timeout: 15000,
    next: { revalidate: 1800 }, // 30d average data can be cached longer
  })
}

/**
 * Fetch historic interest rate for an isolated market
 */
export async function fetchIsolatedAPY(chainId: number, address: string) {
  return indexerFetch(`/api/chains/${chainId}/isolated/${address}/apy`, {
    timeout: 15000,
    next: { revalidate: 300 },
  })
}

/**
 * Fetch user activity history
 */
export async function fetchUserHistory(chainId: number, address: string) {
  return indexerFetch(`/api/chains/${chainId}/users/${address}/history`, {
    timeout: 10000,
    next: { revalidate: 60 }, // Cache for 1 minute
  })
}

/**
 * Fetch referral leaderboard
 */
export async function fetchReferralLeaderboard() {
  return indexerFetch('/api/referrals/leaderboard', {
    timeout: 10000,
    next: { revalidate: 300 },
  })
}

/**
 * Check if a path is expensive (for rate limiting decisions)
 */
export { isExpensivePath }
